import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

import {  catchError, firstValueFrom, map, Observable, of, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { deleteAllCookies, deleteCookieByName, getCookie } from '../../data/utils/cookie-utils';

@Injectable({
  providedIn: 'root'
})
export class Auth0Service {

    private apiUrl: string = environment.baseUrl;
    private userDetails: any = null;
    private config: any = null;
    

    userDetails$: Observable<{ nickname: string; email: string }> = new Observable();

    constructor(
        private http: HttpClient, 
        private toaster: ToastrService,
        private staticAuth: AuthService
    ) {
        
    }

    fetchTenantDetails(tenantCode: string): Observable<any> {
        const headers = { 'tenant-code': tenantCode };
        return this.http.get(`${this.apiUrl}auth/v1/auth-config/`, { headers });
    }

    async redirectToAuth0(tenantCode: string, targetUrl: string): Promise<void> {
        // Fallback to static configuration
          
        try {
          deleteCookieByName(`code`);
          deleteCookieByName(`state`);
            await this.staticAuth.loginWithRedirect({
                authorizationParams: {
                    login_hint: btoa(JSON.stringify({ tenant_code: tenantCode,  original_state: targetUrl})),
                    redirect_uri: window.location.origin + '/platform/callback',
                },
            });
        } catch (error) {
            this.toaster.error('Authentication failed. Please try again.');
        }
    }

    async fetchTenantConfig(tenantCode: string): Promise<any> {
        try {
        const headers = new HttpHeaders({ 'tenant-code': tenantCode });

        const response: any = await this.http.get<any>(`${ this.apiUrl}/auth/v1/auth-config/`, { headers }).toPromise();

        if (response?.success && Array.isArray(response.data) && response.data.length > 0) {
            const valueArray = response.data[0].value;
            const app = valueArray.find((item: any) => item.tenant_code === tenantCode)?.application;

            if (!app) throw new Error('App config missing');

            return {
            domain: app.domain,
            clientId: app.application_id,
            auth0_tenant_name: app.name,
            authorizationParams: {
                redirect_uri: window.location.origin + '/platform/callback'
            }
            };
        }
        return null;
        } catch (error) {
        //   console.error('Failed to fetch tenant config', error);
        return null;
        }
    }

    parseJwt(token: string): any {
        try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(decodeURIComponent(atob(base64)));
        } catch (e) {
        console.error('Error parsing JWT token:', e);
        return {};
        }
    }

    getUserEmail(): string {
        const accessToken = getCookie('access_token');
        // const tenant_code = getCookie('tenant_code');
        if (accessToken) {
            try {
                const decoded: any = jwtDecode(accessToken);

                const email = decoded?.[`https://pf_bp/tenant_data`]?.email || decoded?.email || '';
                return this.capitalizeFirstLetter(email);
            } catch (e) {
                console.error('Error decoding access token:', e);
                return '';
            }
        }
        return '';
    }
    
    private capitalizeFirstLetter(value: string): string {
        return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
    }

    private generateNonce(length: number = 32): string {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const values = window.crypto.getRandomValues(new Uint32Array(length));
        for (let i = 0; i < length; i++) {
            result += charset[values[i] % charset.length];
        }
        return result;
    }

    private async generateCodeChallenge(): Promise<string> {
        const codeVerifier = this.generateNonce(64);
        localStorage.setItem(`pkce_code_verifier`, codeVerifier); // Save for token exchange
    

        // localStorage.setItem(`pkce_code_verifier`, codeVerifier); // Save for token exchange

        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);
        const digest = await window.crypto.subtle.digest('SHA-256', data);
        const base64String = btoa(String.fromCharCode(...new Uint8Array(digest)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        return base64String;
        }

    async login(tenantCode: string, targetUrl: string): Promise<void> {
        
        this.config = {
        domain: environment.auth0.domain,
        clientId:  environment.auth0.clientId,
        tenantCode: tenantCode,
        audience : `https://${environment.auth0.audience}/api/v2/`,
        };  
    
    const codeChallenge = await this.generateCodeChallenge(); // Await here!
    const nonce = this.generateNonce();
    
    const loginHint = btoa(JSON.stringify({ tenant_code: tenantCode, original_state: targetUrl }));
    const state = btoa(JSON.stringify({ tenant_code: tenantCode,  original_state: targetUrl}));
    // Step 5: Build Auth0 URL
    
    const url = `https://${this.config.domain}/authorize?` +
        `client_id=${this.config.clientId}` +
        `&scope=openid%20profile%20email%20offline_access` +
        `&redirect_uri=${encodeURIComponent(window.location.origin + '/platform/callback')}` +
        `&audience=${this.config.audience}` +
        `&login_hint=${encodeURIComponent(loginHint)}` +
        `&response_type=code` +
        `&response_mode=query` +
        `&nonce=${encodeURIComponent(nonce)}` +
        `&code_challenge=${encodeURIComponent(codeChallenge)}` +
        `&code_challenge_method=S256` +
        `&state=${state}`;
    window.location.href = url;
    }

    logoutWithRedirect(): void {
                // Clear local and session storage before redirect
        localStorage.clear();
        sessionStorage.clear();
        deleteAllCookies();
        // Build the Auth0 logout URL
        const auth0Domain = environment.auth0.domain; // e.g., 'dev-xxxxxx.us.auth0.com'
        const clientId = environment.auth0.clientId;
        const returnTo = window.location.origin + '/platform';
        const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${clientId}&returnTo=${encodeURIComponent(returnTo)}`;

        // Redirect to Auth0 logout endpoint
        window.location.href = logoutUrl;
    }

    private getStorageObject(key: string): any {
        try {
            const value = localStorage.getItem(key);
            return value ? JSON.parse(value) : null;
        } catch {
            return null;
        }
    }

    private getFirstStorageObjectBySuffix(suffix: string): any {
        try {
            const keys = Object.keys(localStorage).filter((key) => key.endsWith(suffix));
            if (!keys.length) {
                return null;
            }
            return this.getStorageObject(keys[keys.length - 1]);
        } catch {
            return null;
        }
    }

    private getAuthConfigForRefresh(): { domain: string; clientId: string; audience: string } | null {
        const directConfig = this.getStorageObject('auth0_config');
        const namespacedConfig = this.getFirstStorageObjectBySuffix('_auth0_config');
        const fallback = {
            domain: environment.auth0.domain,
            clientId: environment.auth0.clientId,
            audience: `https://${environment.auth0.audience}/api/v2/`,
        };

        const config = directConfig || namespacedConfig || fallback;
        if (!config?.domain || !config?.clientId || !config?.audience) {
            return null;
        }

        return {
            domain: config.domain,
            clientId: config.clientId,
            audience: config.audience,
        };
    }

    private getTenantDataForRefresh(): any {
        const directTenantData = this.getStorageObject('tenant_data');
        const namespacedTenantData = this.getFirstStorageObjectBySuffix('_tenant_data');
        const cookieTenantData = getCookie('tenant_data');

        if (directTenantData) {
            return directTenantData;
        }
        if (namespacedTenantData) {
            return namespacedTenantData;
        }
        if (cookieTenantData) {
            try {
                return JSON.parse(cookieTenantData);
            } catch {
                return null;
            }
        }
        return null;
    }

    refreshToken(refreshToken: string): Observable<any> {
        const authConfig = this.getAuthConfigForRefresh();
        if (!authConfig) {
            return throwError(() => new Error('Auth0 configuration is missing or invalid.'));
        }

        const tenantData = this.getTenantDataForRefresh();
        const auth0Domain = `https://${authConfig.domain}`;
        const bodyParams: Record<string, string> = {
            grant_type: 'refresh_token',
            client_id: authConfig.clientId,
            refresh_token: refreshToken,
            audience: authConfig.audience,
        };

        if (tenantData?.tenant_id) {
            bodyParams['tenant_id'] = String(tenantData.tenant_id);
        }

        const body = new URLSearchParams(bodyParams);

        return this.http.post<any>(
            `${auth0Domain}/oauth/token`,
            body.toString(),
            {
                headers: new HttpHeaders({
                    'Content-Type': 'application/x-www-form-urlencoded'
                })
            }
        );
    }

        exchangeAuthorizationCodeForTokens(code: string, redirectUri: string): Observable<any> {
        const codeVerifier = localStorage.getItem(`pkce_code_verifier`) || '';
        if (!codeVerifier) {
            return throwError(() => new Error('PKCE code verifier is missing.'));
        }
        const body = new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: environment.auth0.clientId,
                code_verifier: codeVerifier,
                code,
                redirect_uri: redirectUri,
                audience: environment.auth0.audience
                });
 
        return this.http.post<any>(
            `https://${environment.auth0.domain}/oauth/token`,
            body.toString(),
            { headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }) }
        );
        ;
    }
}
